// src/pages/VehicleV2/VehicleTransferList.jsx
// Design template aligned with ProductionBOMListing.jsx

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaSyncAlt,
  FaPlus,
  FaArrowLeft,
  FaFileExport,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleTransferListing`;

/* ---------------- helpers ---------------- */
const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const normalizeSelect = (v, fallback = "Select") => {
  const s = clean(v);
  return s || fallback;
};

const defaultFilters = {
  filterType: "own",
  searchBy: "branch",
  branchName: "",
  fromDate: "",
  toDate: "",
  month: "Select",
  year: "Select",
  vehicleMainType: "Select",
  vehicleCode: "",
  vehicleRegNo: "",
  fromLocation: "Select",
  toLocation: "Select",
  store: "Select",
  vehicleType: "",
};

const VehicleTransferList = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";

  const branchId = useMemo(() => String(toInt(branchIdLS, 8)), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim() || "0", [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userId;

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [months, setMonths] = useState(["Select"]);
  const [years, setYears] = useState([{ id: "Select", name: "Select" }]);

  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);

  const isBusy = pageLoading || dropdownLoading || gridLoading || exporting;

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const makeSelectOptions = useCallback((arr, idKey, nameKey) => {
    const items = Array.isArray(arr) ? arr : [];
    return [{ [idKey]: "Select", [nameKey]: "Select" }, ...items];
  }, []);

  const buildListParams = useCallback(
    (activeFilters) => ({
      filterType: activeFilters.filterType,
      searchBy: activeFilters.searchBy,
      branchName: activeFilters.branchName,
      fromDate: activeFilters.fromDate,
      toDate: activeFilters.toDate,
      month: normalizeSelect(activeFilters.month),
      year: normalizeSelect(activeFilters.year),
      vehicleMainType: normalizeSelect(activeFilters.vehicleMainType),
      vehicleCode: activeFilters.vehicleCode,
      vehicleRegNo: activeFilters.vehicleRegNo,
      fromLocation: normalizeSelect(activeFilters.fromLocation),
      toLocation: normalizeSelect(activeFilters.toLocation),
      store: normalizeSelect(activeFilters.store),
      loginUserId,
      sessionBranchId: branchId,
      branchType,
    }),
    [loginUserId, branchId, branchType]
  );

  const loadDropdowns = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load dropdowns");
      }

      setBranches(data.branches || []);
      setLocations(
        makeSelectOptions(
          data.locations || [],
          "fld_Worklocationid",
          "fld_Worklocationname"
        )
      );
      setStores(
        makeSelectOptions(data.stores || [], "fld_storeid", "fld_storename")
      );
      setVehicleMainTypes(
        makeSelectOptions(
          data.vehicleMainTypes || [],
          "fld_vehicletypeindex",
          "fld_vehicletypename"
        )
      );

      setMonths(
        Array.isArray(data.months) && data.months.length > 0
          ? data.months.map(String)
          : ["Select"]
      );

      setYears(
        Array.isArray(data.years) && data.years.length > 0
          ? data.years
          : [{ id: "Select", name: "Select" }]
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load dropdowns"
      );
    } finally {
      setDropdownLoading(false);
    }
  }, [branchId, commonHeaders, makeSelectOptions]);

  const loadList = useCallback(
    async ({ silent = false, activeFilters = defaultFilters } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(activeFilters),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          if (!silent) toast.warn(data?.message || "No data found");
          return;
        }

        setRows(data.list || []);

        if (!silent) {
          toast.success(
            `Found ${data.count ?? data.list?.length ?? 0} record(s)`
          );
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, buildListParams, commonHeaders, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      await loadDropdowns();
      await loadList({ silent: true, activeFilters: defaultFilters });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  const onFilterChange = useCallback((e) => {
    const { name, value, type } = e.target;
    setFilters((p) => ({
      ...p,
      [name]: type === "radio" ? value : value,
    }));
  }, []);

  const handleSearch = useCallback(async () => {
    setHasSearched(true);
    await loadList({ silent: false, activeFilters: filters });
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = {
      ...defaultFilters,
      filterType: filters.filterType,
    };

    setFilters(reset);
    setHasSearched(true);
    await loadList({
      silent: false,
      activeFilters: reset,
    });
  }, [filters.filterType, loadList]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  const handleClearFilters = useCallback(() => {
    setFilters((p) => ({
      ...p,
      branchName: "",
      fromDate: "",
      toDate: "",
      month: "Select",
      year: "Select",
      vehicleMainType: "Select",
      vehicleCode: "",
      vehicleRegNo: "",
      fromLocation: "Select",
      toLocation: "Select",
      store: "Select",
      vehicleType: "",
      searchBy: "branch",
    }));
    toast.info("Filters cleared");
  }, []);

  const exportExcel = useCallback(async () => {
    try {
      setExporting(true);
      toast.info("Export feature can be connected to backend /export endpoint");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, []);

  const handleAddNew = useCallback(() => {
    navigate("/vehicle/VehicleTransferEntry");
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const recordsCount = useMemo(() => rows.length, [rows]);

  const tableColumns = useMemo(() => {
    if (!rows?.length) return [];
    return Object.keys(rows[0] || {}).slice(0, 12);
  }, [rows]);

  const prettyHeader = useCallback((key) => {
    return String(key)
      .replace(/^fld_/i, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">VEHICLE TRANSFER</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Transfer Entry Listing
              </h2>
              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {branchId || "-"} {" • "} User ID:{" "}
                {loginUserId || "-"} {" • "} Branch Type: {branchType || "-"}
                {" • "} Records: {recordsCount}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                disabled={isBusy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
                type="button"
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={exportExcel}
                disabled={isBusy}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 bg-green-600 hover:bg-green-700 text-white ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
                type="button"
              >
                <FaFileExport />
                {exporting ? "Exporting..." : "Export"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                type="button"
              >
                <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
                Refresh
              </button>

              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
                type="button"
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    type="button"
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    type="button"
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                    type="button"
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="filterType"
                      value="own"
                      checked={filters.filterType === "own"}
                      onChange={onFilterChange}
                      className="form-radio text-blue-600"
                      disabled={isBusy}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Own Vehicle
                    </span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="filterType"
                      value="spares"
                      checked={filters.filterType === "spares"}
                      onChange={onFilterChange}
                      className="form-radio text-blue-600"
                      disabled={isBusy}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Spares & Catalogue
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Branch Name</label>
                  <select
                    name="branchName"
                    value={filters.branchName}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {branches.map((b) => (
                      <option
                        key={b.fld_Branchid}
                        value={String(b.fld_Branchid)}
                      >
                        {b.fld_BranchName || `Branch ${b.fld_Branchid}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {vehicleMainTypes.map((x, idx) => (
                      <option
                        key={idx}
                        value={String(x.fld_vehicletypeindex ?? "Select")}
                      >
                        {String(x.fld_vehicletypename ?? "Select")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select
                    name="month"
                    value={filters.month}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={String(m)}>
                        {String(m)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {years.map((y, idx) => (
                      <option key={idx} value={String(y?.id ?? y)}>
                        {String(y?.name ?? y)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Vehicle Registration Number
                  </label>
                  <input
                    type="text"
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter vehicle registration number"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>From Location</label>
                  <select
                    name="fromLocation"
                    value={filters.fromLocation}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {locations.map((l, idx) => (
                      <option
                        key={idx}
                        value={String(l.fld_Worklocationid ?? "Select")}
                      >
                        {String(l.fld_Worklocationname ?? "Select")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>To Location</label>
                  <select
                    name="toLocation"
                    value={filters.toLocation}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {locations.map((l, idx) => (
                      <option
                        key={idx}
                        value={String(l.fld_Worklocationid ?? "Select")}
                      >
                        {String(l.fld_Worklocationname ?? "Select")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={onFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {stores.map((s, idx) => (
                      <option
                        key={idx}
                        value={String(s.fld_storeid ?? "Select")}
                      >
                        {String(s.fld_storename ?? "Select")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Type (optional)</label>
                  <input
                    type="text"
                    name="vehicleType"
                    value={filters.vehicleType}
                    onChange={onFilterChange}
                    className={inputClass}
                    placeholder="(optional)"
                    disabled={isBusy}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className={labelClass}>Search By</label>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="searchBy"
                      value="branch"
                      checked={filters.searchBy === "branch"}
                      onChange={onFilterChange}
                      className="form-radio text-blue-600"
                      disabled={isBusy}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Branch
                    </span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="searchBy"
                      value="location"
                      checked={filters.searchBy === "location"}
                      onChange={onFilterChange}
                      className="form-radio text-blue-600"
                      disabled={isBusy}
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Location
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : "Loading..."}
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {hasSearched
                    ? "No Records found"
                    : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      S.No
                    </th>
                    {tableColumns.map((c) => (
                      <th
                        key={c}
                        className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap"
                      >
                        {prettyHeader(c)}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={`${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  } divide-y`}
                >
                  {rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                      }`}
                    >
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {idx + 1}
                      </td>
                      {tableColumns.map((col) => (
                        <td
                          key={col}
                          className={`px-6 py-4 text-sm whitespace-nowrap ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {String(row?.[col] ?? "-")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== BACK ==================== */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              } ${isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
              type="button"
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransferList;